import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { useCompanyUsers } from '@/common/hooks/queries/useCompanyUsers'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import { TicketData, TicketDataTableItem } from '@/common/types/support/ticket.type'
import { useAvailableCompanyBranches } from '@/features/companyBranches/hooks/useAvailableCompanyBranches'
import { CompanyUserData } from '@/features/companyBranches/types/companyUsers.type'
import { defaultColumns } from '@/features/support/constants/columns.constant'
import { options as statusOptions } from '@/features/support/constants/statusOptions.constant'
import { useSupportTickets } from '@/features/support/hooks/useSupportTickets'
import { SupportTicketsTransformer } from '@/features/support/services/supportTicketsTransformer'
import { SearchIcon, TicketIcon } from 'lucide-react'
import { Option } from "@/common/types/option";

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [searchName, setSearchName] = useDebounce(filterParams.name || '')
  const [searchUser, setSearchUser] = useDebounce('');
  const [searchBranch, setSearchBranch] = useDebounce('');

  const { data: usersOptions } = useCompanyUsers({
    filters: { search: searchUser },
    mapper: Mapper.mapToOptions<CompanyUserData>({ valueFieldName: 'id', labelFieldName: 'full_name' }),
  });

  const { data: branchesOptions } = useAvailableCompanyBranches({
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const {
    data: supportTickets,
    meta,
    isFetching: supportTicketsIsFetching,
    error: supportTicketsError = {} as any,
    refetch: refetchSupportTickets,
  } = useSupportTickets({
    filters: {
      page: filterParams.page || 1,
      search: searchName,
      status: filterParams.status?.value || '',
      authorId: filterParams.author?.value,
      branchId: filterParams.branch?.value
    },
    mapper: Mapper.mapWithTransformer<TicketData[], TicketDataTableItem[]>({
      transformer: SupportTicketsTransformer.tableTransformer(),
    }),
  })

  return (
    <div>
      <div className="flex flex-col gap-6">
        <ListHeader
          Icon={<TicketIcon className="size-10" strokeWidth={1.5} />}
          title="Chamados"
          createButtonText="Abrir chamado"
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-128px)]"
          statusCode={supportTicketsError?.status}
          refetchFunction={refetchSupportTickets}
        >
          <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <TextField
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                value={filterParams.name}
                onChange={(e: any) => {
                  setSearchName(e.target?.value)
                  setFilterParams((prev) => ({
                    ...prev,
                    name: e.target?.value,
                  }))
                }}
              />

              <ListFilters
                filters={[
                  {
                    className: 'w-auto',
                    onChange: (e: any) =>
                      setFilterParams((params) => ({
                        ...params,
                        status: e.value ? e : undefined,
                      })),
                    value: filterParams.status,
                    options: [{ label: 'Todos', value: '' }, ...(statusOptions || [])],
                    placeholder: 'Status',
                  },
                  {
                    className: 'min-w-40',
                    placeholder: 'Autor',
                    options: [{ label: 'Todos', value: '' }, ...(usersOptions || [])],
                    onChange: (e: any) =>
                      setFilterParams((params) => ({ 
                        ...params, 
                        author: e.value ? e : undefined 
                      })),
                    onInputChange: setSearchUser,
                    value: filterParams.author || '',
                  },
                  {
                    className: 'min-w-40',
                    placeholder: 'Filial',
                    options: [{ label: 'Todos', value: '' }, ...branchesOptions as Option[]],
                    onChange: (e: any) =>
                      setFilterParams((prev) => ({ 
                        ...prev, 
                        branch: e.value ? e : undefined 
                      })),
                    onInputChange: setSearchBranch,
                    value: filterParams.branch || '',
                  },
                ]}
              />
            </div>

            <Table
              className="max-h-[calc(100vh-252px)]"
              columns={defaultColumns}
              isLoadingData={supportTicketsIsFetching}
              page={filterParams.page || 1}
              rowEmptyValue="--"
              rows={supportTickets}
              totalPages={meta.total_pages}
              totalCount={meta.total}
              hasPagination
              setPage={(p) => setFilterParams((params) => ({ ...params, page: p }))}
            />
          </div>
        </ErrorMessageWrapper>
      </div>
    </div>
  )
}

export default List
